import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { isDevAffordanceEnabled } from '../../utils/devMode';
import { XIcon, ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, SearchIcon, RefreshIcon } from '@heroicons/react/outline';
import { InformationCircleIcon } from '@heroicons/react/solid';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import RmsGaugeChart from './RmsGaugeChart';
import InfoTooltip from '../InfoTooltip';

// Import shared risk stratification utilities and constants
// Note: Additional utilities (DIALOG_DIMENSIONS, calculateMaxContentHeight, calculateAvailableHeight,
// getRespondentLabel, useRiskStratificationSchemas, extractFormKeys) are available for future refactoring
import {
  TIER_CONFIG,
  DOMAIN_CONFIG,
  SCORE_LABELS,
  RISK_LEVEL_OPTIONS,
  scoreToRiskLevel,
  tierToRiskLevel,
  formKeyToTitle,
  groupIndicatorsByRespondent,
  sortDomainsByOrder,
  calculateIndicatorStats,
  getBadgeStyleForLevel,
} from '../../utils/riskStratification';

// Environment check for showing dev tools (Raw JSON accordion).
// Only visible in local development only.
const isDev = isDevAffordanceEnabled();

/**
 * RiskStratificationDialog
 * ------------------------
 * Purpose:
 *   Full-screen modal showing detailed risk stratification breakdown.
 *   Displays the overall tier, per-domain summaries with indicators,
 *   and a collapsible raw JSON section for development/debugging.
 *
 * Props:
 *   - isOpen: boolean - Controls dialog visibility
 *   - onClose: () => void - Callback to close the dialog
 *   - riskData: The riskStratification object from the patient record
 *
 * Layout:
 *   - Left panel: Overall tier with gauge
 *   - Right panel: Per-domain breakdown with indicator lists
 *   - Bottom: Collapsible accordion with raw JSON (dev mode)
 */

// Note: TIER_CONFIG, DOMAIN_CONFIG, SCORE_LABELS, and helper functions
// are now imported from '../../utils/riskStratification'

// Note: groupIndicatorsByRespondent is now imported from '../../utils/riskStratification'

/**
 * Helper function to summarize indicators by score level for a group.
 * Returns an array of { score, label, count } objects for non-zero counts.
 */
const summarizeByScore = (indicators) => {
  const counts = { 4: 0, 3: 0, 2: 0 }; // Only count elevated risks (not Standard=1)
  
  indicators.forEach(ind => {
    if (ind.score >= 2 && ind.score <= 4) {
      counts[ind.score]++;
    }
  });
  
  // Build summary array, highest score first
  const summary = [];
  if (counts[4] > 0) summary.push({ score: 4, label: 'Urgent', count: counts[4] });
  if (counts[3] > 0) summary.push({ score: 3, label: 'High', count: counts[3] });
  if (counts[2] > 0) summary.push({ score: 2, label: 'Medium', count: counts[2] });
  
  return summary;
};

/**
 * DomainDetailContent
 * -------------------
 * Inline content component showing detailed indicator information for a risk domain.
 * This is used within the main RiskStratificationDialog when navigating to domain details.
 * 
 * Props:
 *   - domainName: string - The risk domain name
 *   - indicators: array - List of indicator objects
 *   - orgId: string - Organisation ID for fetching schemas
 *   - respondentNames: object - { guardian: { firstName, lastName, relationshipLabel }, school_user: { firstName, lastName, role } }
 *   - pendingReviewByQuestion: object - Map of "questionId:formKey:respondentRole" to array of pending items
 */
const DomainDetailContent = ({ domainName, indicators, orgId, respondentNames, pendingReviewByQuestion = {}, onContentHeightChange, onNavigateToManualReview }) => {
  const [schemas, setSchemas] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedOptions, setExpandedOptions] = useState({});
  
  // Currently selected respondent tab: 'guardian' or 'school'
  const [selectedTab, setSelectedTab] = useState('guardian');
  
  // Refs for measuring content heights of both tabs to maintain consistent height
  const guardianContentRef = useRef(null);
  const schoolContentRef = useRef(null);
  // Ref for the main scrollable content area (to scroll to top on tab switch)
  const contentAreaRef = useRef(null);
  // maxContentHeight is set for internal tracking but read via onContentHeightChange callback
  const [, setMaxContentHeight] = useState(null);
  
  /**
   * Handler for switching tabs that also scrolls content to top.
   * This provides a better UX when switching between Parent/Carer and School tabs.
   */
  const handleTabChange = useCallback((tabId) => {
    setSelectedTab(tabId);
    // Scroll content area to top when switching tabs
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTop = 0;
    }
  }, []);
  
  /**
   * Toggle the expanded state of options for a specific question card.
   */
  const toggleOptionsExpanded = (cardKey) => {
    setExpandedOptions(prev => ({
      ...prev,
      [cardKey]: !prev[cardKey]
    }));
  };
  
  /**
   * Get the role label for a respondent (e.g., "Foster parent", "Teacher").
   * Uses the relationshipLabel for guardians or role for school users.
   * Falls back to "Parent / Carer" or "School Representative" if not available.
   */
  const getRespondentRoleLabel = (role) => {
    if (role === 'guardian') {
      const guardian = respondentNames?.guardian;
      const roleLabel = guardian?.relationshipLabel || null;
      return roleLabel || 'Parent / Carer';
    } else if (role === 'school_user' || role === 'school') {
      const school = respondentNames?.school_user;
      const roleLabel = school?.role || null;
      return roleLabel || 'School Representative';
    }
    return role;
  };
  
  // Get unique form keys from indicators AND pending review items to fetch schemas
  const formKeys = useMemo(() => {
    const keys = new Set();
    
    indicators.forEach(ind => {
      if (ind.formKey) {
        const baseKey = ind.formKey.replace(/_guardian|_school_user/g, '');
        keys.add(baseKey);
      }
    });
    
    Object.values(pendingReviewByQuestion).forEach(pendingItems => {
      pendingItems.forEach(item => {
        if (item.formKey) {
          const baseKey = item.formKey.replace(/_guardian|_school_user/g, '');
          keys.add(baseKey);
        }
      });
    });
    
    return Array.from(keys);
  }, [indicators, pendingReviewByQuestion]);
  
  // Fetch schemas when component mounts or indicators change
  // Includes retry logic with exponential backoff for transient errors (e.g., deadline-exceeded)
  useEffect(() => {
    if (!orgId || formKeys.length === 0) return;
    
    // Helper function to fetch a single schema with retry logic
    // Retries up to 3 times with exponential backoff (1s, 2s, 4s delays)
    const fetchSchemaWithRetry = async (getSchema, formKey, maxRetries = 3) => {
      let lastError = null;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const result = await getSchema({ orgId, formKey });
          if (result.data?.schema) {
            return result.data.schema;
          }
          return null;
        } catch (err) {
          lastError = err;
          const isRetryable = err.code === 'deadline-exceeded' || 
                              err.code === 'unavailable' ||
                              err.message?.includes('deadline-exceeded');
          
          if (isRetryable && attempt < maxRetries - 1) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, attempt) * 1000;
            console.warn(`[DomainDetailContent] Retrying ${formKey} (attempt ${attempt + 2}/${maxRetries}) after ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            // Non-retryable error or max retries reached
            throw err;
          }
        }
      }
      
      throw lastError;
    };
    
    const fetchSchemas = async () => {
      setLoading(true);
      const getSchema = httpsCallable(functions, 'rmsGetFormSchema');
      const newSchemas = {};
      
      try {
        // Fetch schemas sequentially to avoid overwhelming Firebase Functions
        // This also helps with cold start issues as only one function warms up at a time
        for (const formKey of formKeys) {
          try {
            const schema = await fetchSchemaWithRetry(getSchema, formKey);
            if (schema) {
              newSchemas[formKey] = schema;
            }
          } catch (err) {
            console.error(`[DomainDetailContent] Error fetching schema for ${formKey}:`, err);
            // Continue with other schemas even if one fails
          }
        }
        
        setSchemas(newSchemas);
      } catch (err) {
        console.error('[DomainDetailContent] Error fetching schemas:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSchemas();
  }, [orgId, formKeys]);
  
  /**
   * Find all options for a question from the schema.
   */
  const getQuestionOptions = (indicatorOrItem) => {
    if (!indicatorOrItem) return null;
    
    const baseFormKey = indicatorOrItem.formKey?.replace(/_guardian|_school_user/g, '');
    const schema = schemas[baseFormKey];
    if (!schema) return null;
    
    for (const section of schema.sections || []) {
      for (const question of section.questions || []) {
        if (question.questionId === indicatorOrItem.questionId) {
          return question.options || [];
        }
      }
    }
    return null;
  };
  
  const grouped = groupIndicatorsByRespondent(indicators);
  
  /**
   * Group indicators by question (questionId + formKey).
   * For multi-select questions, multiple options from the same question are grouped together.
   * 
   * Also tracks:
   * - maxScore: highest risk score (for primary sorting)
   * - secondScore: second highest risk score (for secondary sorting)
   * - allScores: array of all scores for sorting purposes
   * 
   * Wrapped in useCallback to satisfy React hooks exhaustive-deps rule.
   */
  const groupIndicatorsByQuestion = useCallback((indicatorList, respondentRole = 'guardian') => {
    const groups = {};
    
    // First, add all scored indicators
    indicatorList.forEach(ind => {
      const key = `${ind.questionId}:${ind.formKey}`;
      
      if (!groups[key]) {
        groups[key] = {
          questionId: ind.questionId,
          questionLabel: ind.questionLabel,
          formKey: ind.formKey,
          maxScore: ind.score,
          allScores: [], // Track all scores for secondary sorting
          selectedOptions: [],
          hasPendingReview: false,
          _firstIndicator: ind,
        };
      }
      
      if (ind.score > groups[key].maxScore) {
        groups[key].maxScore = ind.score;
      }
      
      // Track all scores for secondary sorting
      if (ind.score !== null && ind.score !== undefined) {
        groups[key].allScores.push(ind.score);
      }
      
      groups[key].selectedOptions.push({
        optionId: ind.optionId,
        optionLabel: ind.optionLabel,
        score: ind.score,
        freeText: ind.freeText,
        isManuallyAssigned: ind.isManuallyAssigned,
        assignedByName: ind.assignedByName,
        assignedAt: ind.assignedAt,
        needsReview: false,
      });
    });
    
    // Now, add pending review items that match this domain and respondent
    Object.entries(pendingReviewByQuestion).forEach(([lookupKey, pendingItems]) => {
      const [questionId, formKey, itemRespondentRole] = lookupKey.split(':');
      
      if (itemRespondentRole !== respondentRole) return;
      
      const firstItem = pendingItems[0];
      if (firstItem?.domainHint !== domainName) return;
      
      const groupKey = `${questionId}:${formKey}`;
      
      pendingItems.forEach(item => {
        if (groups[groupKey]) {
          const alreadyExists = groups[groupKey].selectedOptions.some(
            opt => opt.optionId === item.optionId
          );
          if (alreadyExists) return;
        }
        
        if (!groups[groupKey]) {
          groups[groupKey] = {
            questionId: item.questionId,
            questionLabel: item.questionLabel,
            formKey: item.formKey,
            maxScore: -1,
            allScores: [],
            selectedOptions: [],
            hasPendingReview: true,
            _firstIndicator: null,
            _pendingItem: item,
          };
        }
        
        groups[groupKey].hasPendingReview = true;
        
        groups[groupKey].selectedOptions.push({
          optionId: item.optionId,
          optionLabel: item.optionLabel,
          score: null,
          freeText: item.freeText,
          isManuallyAssigned: false,
          assignedByName: null,
          assignedAt: null,
          needsReview: true,
        });
      });
    });
    
    return Object.values(groups);
  }, [pendingReviewByQuestion, domainName]);
  
  /**
   * Sort question groups by risk level:
   * 1. Primary: highest risk score (descending)
   * 2. Secondary: second highest risk score (descending)
   * 3. Tertiary: alphabetical by question label
   * 
   * Groups with only pending review items (maxScore = -1) go at the end.
   */
  const sortByRiskLevel = (questionGroups) => {
    return [...questionGroups].sort((a, b) => {
      // Items with only pending review (maxScore = -1) go last
      if (a.maxScore === -1 && b.maxScore !== -1) return 1;
      if (a.maxScore !== -1 && b.maxScore === -1) return -1;
      
      // Primary sort: highest score (descending)
      if (a.maxScore !== b.maxScore) {
        return b.maxScore - a.maxScore;
      }
      
      // Secondary sort: second highest score (descending)
      const aScoresSorted = [...a.allScores].sort((x, y) => y - x);
      const bScoresSorted = [...b.allScores].sort((x, y) => y - x);
      const aSecondScore = aScoresSorted[1] ?? -1;
      const bSecondScore = bScoresSorted[1] ?? -1;
      
      if (aSecondScore !== bSecondScore) {
        return bSecondScore - aSecondScore;
      }
      
      // Tertiary: alphabetical by question label
      return (a.questionLabel || '').localeCompare(b.questionLabel || '');
    });
  };
  
  // Process guardian and school indicators separately, then sort
  const guardianGroups = useMemo(() => {
    const groups = groupIndicatorsByQuestion(grouped.guardian, 'guardian');
    return sortByRiskLevel(groups);
  }, [grouped.guardian, groupIndicatorsByQuestion]);
  
  const schoolGroups = useMemo(() => {
    const groups = groupIndicatorsByQuestion(grouped.school, 'school_user');
    return sortByRiskLevel(groups);
  }, [grouped.school, groupIndicatorsByQuestion]);
  
  /**
   * Measure both tab contents after render and use the max height.
   * This ensures the container doesn't resize when switching between tabs,
   * providing a stable viewing experience.
   * Uses minimal delay so height animates in parallel with page transitions.
   */
  useLayoutEffect(() => {
    if (loading) return;
    
    // Minimal delay to ensure DOM is updated, height animates in parallel
    const measureTimer = setTimeout(() => {
      const guardianHeight = guardianContentRef.current?.scrollHeight || 0;
      const schoolHeight = schoolContentRef.current?.scrollHeight || 0;
      const maxHeight = Math.max(guardianHeight, schoolHeight);
      
      if (maxHeight > 0) {
        setMaxContentHeight(maxHeight);
        // Notify parent of content height for dynamic dialog sizing
        if (onContentHeightChange) {
          onContentHeightChange(maxHeight);
        }
      }
    }, 10);
    
    return () => clearTimeout(measureTimer);
  }, [loading, guardianGroups, schoolGroups, onContentHeightChange]);
  
  /**
   * Render a question card.
   */
  const renderQuestionCard = (questionGroup, idx, respondentType) => {
    const lookupSource = questionGroup._firstIndicator || questionGroup._pendingItem;
    const options = getQuestionOptions(lookupSource);
    const hasOptions = options && options.length > 0;
    const submittedAt = questionGroup._firstIndicator?.submittedAt || questionGroup._pendingItem?.submittedAt;
    const cardKey = `${respondentType}-${questionGroup.questionId}-${idx}`;
    const isExpanded = expandedOptions[cardKey] || false;
    
    // Sort options: scored items by score (highest first), then needsReview items at the end
    const sortedOptions = [...questionGroup.selectedOptions].sort((a, b) => {
      if (a.needsReview && !b.needsReview) return 1;
      if (!a.needsReview && b.needsReview) return -1;
      if (a.needsReview && b.needsReview) return (a.optionLabel || '').localeCompare(b.optionLabel || '');
      return (b.score || 0) - (a.score || 0);
    });
    
    return (
      <div key={cardKey} className="border rounded-lg p-4 bg-primary/5 border-primary/20">
        {/* Header: Question text */}
        <div className="mb-3">
          <div className="text-sm font-medium text-gray-900">
            {questionGroup.questionLabel || 'Question not available'}
          </div>
        </div>
        
        {/* View all options accordion */}
        {hasOptions && (
          <div className="mb-3">
            <button
              type="button"
              onClick={() => toggleOptionsExpanded(cardKey)}
              className="text-xs text-gray-500 cursor-pointer hover:text-gray-600 flex items-center gap-1 transition-colors"
            >
              <ChevronDownIcon 
                className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
              />
              {isExpanded ? 'Hide' : 'View all'} {options.length} options
            </button>
            <div 
              className="grid transition-all duration-200 ease-in-out"
              style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {options.map((opt) => {
                    const isSelected = questionGroup.selectedOptions.some(s => s.optionId === opt.optionId);
                    return (
                      <span
                        key={opt.optionId}
                        className={`text-xs px-2 py-1 rounded ${
                          isSelected
                            ? 'bg-primary/10 border border-primary/30 text-primary font-medium'
                            : 'bg-gray-100 border border-gray-200 text-gray-500'
                        }`}
                      >
                        {opt.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Selected answers */}
        <div className="mb-3 space-y-1.5">
          {sortedOptions.map((opt, optIdx) => {
            const optScoreConfig = opt.needsReview
              ? SCORE_LABELS['needsReview']
              : (SCORE_LABELS[opt.score] || SCORE_LABELS[1]);
            
            return (
              <div key={optIdx} className="flex items-center gap-2">
                {/* Badge is clickable for needsReview items - navigates to Manual Review */}
                {opt.needsReview && onNavigateToManualReview ? (
                  <button
                    type="button"
                    onClick={() => onNavigateToManualReview(opt.respondentRole || 'guardian')}
                    className="flex-shrink-0 inline-flex items-center justify-center w-24 h-6 rounded text-[11px] font-semibold cursor-pointer hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: optScoreConfig.bg,
                      color: optScoreConfig.captionColor,
                      border: `1px solid ${optScoreConfig.captionColor}40`,
                    }}
                    title="Click to review this answer"
                  >
                    {optScoreConfig.label}
                  </button>
                ) : (
                  <span
                    className="flex-shrink-0 inline-flex items-center justify-center w-24 h-6 rounded text-[11px] font-semibold"
                    style={{
                      backgroundColor: optScoreConfig.bg,
                      color: optScoreConfig.captionColor,
                      border: `1px solid ${optScoreConfig.captionColor}40`,
                    }}
                  >
                    {optScoreConfig.label}
                  </span>
                )}
                <div className="flex-grow">
                  <div className="text-sm text-gray-800">
                    {opt.optionLabel}{opt.freeText && `: `}
                    {opt.freeText && (
                      <span className="italic text-gray-800">"{opt.freeText}"</span>
                    )}
                  </div>
                  {opt.isManuallyAssigned && opt.assignedByName && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      Assigned by {opt.assignedByName}
                      {opt.assignedAt && (
                        <span> on {new Date(opt.assignedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(opt.assignedAt).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end pt-2 border-t border-gray-100 text-xs text-gray-500">
          {questionGroup.formKey && (
            <span>
              {formKeyToTitle(questionGroup.formKey)}
              {submittedAt && (
                <span>
                  {' '}submitted on {new Date(submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(submittedAt).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };
  
  // Calculate summary counts for each respondent (for the headline)
  // We need to count elevated risks (Urgent, High, Medium) from all indicators
  const guardianSummary = useMemo(() => {
    return summarizeByScore(grouped.guardian);
  }, [grouped.guardian]);
  
  const schoolSummary = useMemo(() => {
    return summarizeByScore(grouped.school);
  }, [grouped.school]);
  
  /**
   * Render the summary headline for a respondent.
   * Format: "{Role} reported risk indicators: X Urgent, Y High and Z Medium"
   * OR: "{Role} reported no elevated risk indicators"
   */
  const renderSummaryHeadline = (respondentType) => {
    const scoreSummary = respondentType === 'guardian' ? guardianSummary : schoolSummary;
    const roleLabel = getRespondentRoleLabel(respondentType === 'guardian' ? 'guardian' : 'school_user');
    
    if (scoreSummary.length === 0) {
      return (
        <div className="text-sm text-gray-700">
          <span className="font-semibold text-gray-900">{roleLabel}</span>
          {' '}reported no elevated risk indicators
        </div>
      );
    }
    
    // Build parts with count and styled badge
    const parts = scoreSummary.map(({ score, label, count }, idx) => {
      const config = SCORE_LABELS[score];
      return (
        <span key={score} className="inline-flex items-center gap-1">
          <span className="font-semibold">{count}</span>
          <span
            className="px-1.5 py-0.5 rounded text-xs font-medium"
            style={{ backgroundColor: config.bg, color: config.captionColor }}
          >
            {label}
          </span>
          {idx < scoreSummary.length - 2 && <span className="text-gray-500">,</span>}
          {idx === scoreSummary.length - 2 && <span className="text-gray-500 ml-0.5">and</span>}
        </span>
      );
    });
    
    return (
      <div className="text-sm text-gray-700 flex flex-wrap items-baseline gap-1">
        <span className="font-semibold text-gray-900">{roleLabel}</span>
        <span>reported risk indicators:</span>
        {parts}
      </div>
    );
  };
  
  // Tab items for the sidebar
  const tabs = [
    { id: 'guardian', label: 'Parent / Carer', count: guardianGroups.length },
    { id: 'school', label: 'School', count: schoolGroups.length },
  ];
  
  // Render the content with sidebar navigation
  // Container uses full height of parent, overflow-hidden ensures only main content scrolls
  return (
    <div className="flex overflow-hidden h-full">
      {/* Sidebar Navigation - fixed, does not scroll */}
      <div className="w-52 flex-shrink-0 border-r border-gray-200 bg-gradient-to-b from-primary/5 to-primary/10">
        <div className="p-4 pt-6">
          <nav className="space-y-1.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between
                  transition-all duration-200 ease-in-out
                  ${selectedTab === tab.id
                    ? 'bg-white shadow-sm border border-gray-200 text-primary'
                    : 'text-gray-600 hover:bg-white/70 hover:text-gray-800 border border-transparent'
                  }`}
              >
                {/* Use consistent font-weight to avoid layout shift - always medium weight but vary opacity/color */}
                <span className={`font-medium transition-colors duration-200 ${selectedTab === tab.id ? 'text-primary' : 'text-gray-600'}`}>
                  {tab.label}
                </span>
                {/* Fixed-size badge for consistent layout with double digits */}
                <span 
                  className={`text-xs min-w-[28px] h-5 flex items-center justify-center rounded-full
                    transition-colors duration-200 ease-in-out
                    ${selectedTab === tab.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'bg-gray-200 text-gray-500'
                    }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Main Content Area - renders both tabs to measure, shows only selected */}
      {/* Always allow vertical scrolling when content overflows */}
      <div 
        ref={contentAreaRef}
        className="flex-1 overflow-x-hidden overflow-y-auto p-6 relative"
      >
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshIcon className="w-6 h-6 text-gray-500 animate-spin" />
            <span className="ml-2 text-gray-600">Loading question details...</span>
          </div>
        )}
        
        {!loading && (
          <div className="relative">
            {/* Guardian tab content */}
            <div 
              ref={guardianContentRef}
              className={`transition-opacity duration-200 ${
                selectedTab === 'guardian' 
                  ? 'opacity-100' 
                  : 'opacity-0 absolute inset-0 pointer-events-none'
              }`}
            >
              <div className="mb-4 pb-4 border-b border-gray-200">
                {renderSummaryHeadline('guardian')}
              </div>
              {guardianGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <InformationCircleIcon className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm">No indicators reported by {getRespondentRoleLabel('guardian')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {guardianGroups.map((qg, idx) => renderQuestionCard(qg, idx, 'guardian'))}
                </div>
              )}
            </div>
            
            {/* School tab content */}
            <div 
              ref={schoolContentRef}
              className={`transition-opacity duration-200 ${
                selectedTab === 'school' 
                  ? 'opacity-100' 
                  : 'opacity-0 absolute inset-0 pointer-events-none'
              }`}
            >
              <div className="mb-4 pb-4 border-b border-gray-200">
                {renderSummaryHeadline('school')}
              </div>
              {schoolGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <InformationCircleIcon className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm">No indicators reported by {getRespondentRoleLabel('school_user')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {schoolGroups.map((qg, idx) => renderQuestionCard(qg, idx, 'school'))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


/**
 * ManualReviewContent
 * -------------------
 * Inline content component for reviewing and assigning risk levels to manual review items.
 * This is rendered within the main RiskStratificationDialog when navigating to manual review.
 * 
 * REDESIGNED UI:
 * - Shows all selected answers with risk level badges on the LEFT and answer text on the RIGHT
 * - For answers needing review, shows 5 radio-style options below for risk level selection
 * - User must select a risk level for ALL items in a question card before confirming
 * - "Confirm" button is disabled until all selections are made
 * - On confirm, batch-submits all decisions for that question card
 * 
 * Props:
 *   - manualReviewItems: array - List of manual review item objects
 *   - respondentNames: object - { guardian: { ... }, school_user: { ... } }
 *   - orgId: string - Organisation ID
 *   - patientRecordId: string - Patient record ID
 *   - onRiskAssigned: function - Callback when risk levels are assigned
 */
const ManualReviewContent = ({
  manualReviewItems,
  riskData,
  respondentNames,
  orgId,
  patientRecordId,
  onRiskAssigned,
  onContentHeightChange,
  initialTab = null, // Optional: force initial tab selection ('guardian' or 'school')
  canReview = false, // TD-51: only the committed triage clinician may flag; else read-only
}) => {
  const readOnly = canReview !== true;
  const [schemas, setSchemas] = useState({});
  const [loading, setLoading] = useState(false);
  // Track whether initial schema loading has completed (to avoid measuring height before schemas are ready)
  const [schemasReady, setSchemasReady] = useState(false);
  const [expandedOptions, setExpandedOptions] = useState({});
  
  // Track pending selections for each item (before confirm)
  // Key: "questionId:optionId:formKey:respondentRole", Value: selected risk level string
  const [pendingSelections, setPendingSelections] = useState({});
  
  // Track which question cards are currently being submitted
  const [submittingCards, setSubmittingCards] = useState({});
  const [submitError, setSubmitError] = useState(null);
  // Track which cards are in the "exiting" animation state (after successful confirmation)
  const [exitingCards, setExitingCards] = useState({});
  
  // Refs for measuring content heights of both tabs
  const guardianContentRef = useRef(null);
  const schoolContentRef = useRef(null);
  // Ref for the main scrollable content area (to scroll to top on tab switch)
  const contentAreaRef = useRef(null);
  // maxContentHeight is set for internal tracking but read via onContentHeightChange callback
  const [, setMaxContentHeight] = useState(null);

  // State for showing methodology guide
  const [showMethodology, setShowMethodology] = useState(false);
  
  // Group items by respondent early so we can determine default tab
  const guardianItems = manualReviewItems?.filter(item => item.respondentRole === 'guardian') || [];
  const schoolItems = manualReviewItems?.filter(item => item.respondentRole === 'school_user') || [];
  
  // Determine default tab based on initialTab prop or which has items
  // Priority: 1) initialTab if provided, 2) school if guardian has 0 but school has >0, 3) guardian
  const defaultTab = useMemo(() => {
    if (initialTab) return initialTab;
    return guardianItems.length === 0 && schoolItems.length > 0 ? 'school' : 'guardian';
  }, [initialTab, guardianItems.length, schoolItems.length]);
  
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  
  // Update selectedTab when initialTab changes (e.g., navigating from indicator details)
  useEffect(() => {
    if (initialTab) {
      setSelectedTab(initialTab);
    }
  }, [initialTab]);
  
  /**
   * Handler for switching tabs that also scrolls content to top.
   * This provides a better UX when switching between Parent/Carer and School tabs.
   */
  const handleTabChange = useCallback((tabId) => {
    setSelectedTab(tabId);
    setShowMethodology(false);
    // Scroll content area to top when switching tabs
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTop = 0;
    }
  }, []);
  
  /**
   * Toggle the expanded state of options for a specific question card.
   */
  const toggleOptionsExpanded = (cardKey) => {
    setExpandedOptions(prev => ({
      ...prev,
      [cardKey]: !prev[cardKey]
    }));
  };
  
  /**
   * Get the role label for a respondent.
   * Falls back to "Parent / Carer" or "School Representative" if not available.
   */
  const getRespondentRoleLabel = (role) => {
    if (role === 'guardian') {
      const guardian = respondentNames?.guardian;
      return guardian?.relationshipLabel || 'Parent / Carer';
    } else if (role === 'school_user' || role === 'school') {
      const school = respondentNames?.school_user;
      return school?.role || 'School Representative';
    }
    return role || 'Unknown';
  };
  
  // Fetch schemas for form keys present in manual review items
  // Includes retry logic with exponential backoff for transient errors (e.g., deadline-exceeded)
  useEffect(() => {
    if (!orgId || !manualReviewItems?.length) {
      // No items to review, mark schemas as ready (nothing to fetch)
      setSchemasReady(true);
      return;
    }
    
    const formKeys = [...new Set(manualReviewItems.map(item => item.formKey))];
    const missingKeys = formKeys.filter(key => !schemas[key]);
    
    if (missingKeys.length === 0) {
      // All schemas already cached, mark as ready
      setSchemasReady(true);
      return;
    }
    
    // Helper function to fetch a single schema with retry logic
    // Retries up to 3 times with exponential backoff (1s, 2s, 4s delays)
    const fetchSchemaWithRetry = async (getSchema, formKey, maxRetries = 3) => {
      let lastError = null;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const result = await getSchema({ orgId, formKey });
          if (result.data?.schema) {
            return result.data.schema;
          }
          return null;
        } catch (err) {
          lastError = err;
          const isRetryable = err.code === 'deadline-exceeded' || 
                              err.code === 'unavailable' ||
                              err.message?.includes('deadline-exceeded');
          
          if (isRetryable && attempt < maxRetries - 1) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, attempt) * 1000;
            console.warn(`[ManualReviewContent] Retrying ${formKey} (attempt ${attempt + 2}/${maxRetries}) after ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            throw err;
          }
        }
      }
      
      throw lastError;
    };
    
    const fetchSchemas = async () => {
      setLoading(true);
      const getSchema = httpsCallable(functions, 'rmsGetFormSchema');
      const newSchemas = { ...schemas };
      
      // Fetch schemas sequentially to avoid overwhelming Firebase Functions
      for (const formKey of missingKeys) {
        try {
          const schema = await fetchSchemaWithRetry(getSchema, formKey);
          if (schema) {
            newSchemas[formKey] = schema;
          }
        } catch (err) {
          console.error(`[ManualReviewContent] Failed to fetch schema for ${formKey}:`, err);
          // Continue with other schemas even if one fails
        }
      }
      
      setSchemas(newSchemas);
      setLoading(false);
      setSchemasReady(true); // Mark schemas as ready after fetch completes
    };
    
    fetchSchemas();
  }, [orgId, manualReviewItems, schemas]);
  
  /**
   * Get all options for a question from the schema.
   */
  const getQuestionOptions = (item) => {
    const schema = schemas[item?.formKey];
    if (!schema) return null;
    
    for (const section of schema.sections || []) {
      for (const question of section.questions || []) {
        if (question.questionId === item.questionId) {
          return question.options || [];
        }
      }
    }
    return null;
  };
  
  /**
   * Get auto-scored indicators for a specific question from risk data.
   * These are answers that have been automatically scored (not needing manual review).
   * Used to provide context to clinicians when reviewing manual items.
   * 
   * @param {string} questionId - The question ID to find indicators for
   * @param {string} formKey - The form key to match
   * @param {string} respondentRole - The respondent role to match ('guardian' or 'school_user')
   * @returns {Array} Array of auto-scored indicator objects with score, optionLabel, and freeText
   */
  const getAutoScoredAnswersForQuestion = (questionId, formKey, respondentRole) => {
    if (!riskData?.domainSummaries) return [];
    
    const autoScoredAnswers = [];
    
    // Search through all domain summaries for indicators matching this question
    Object.values(riskData.domainSummaries).forEach((domainSummary) => {
      const indicators = domainSummary.indicators || [];
      
      indicators.forEach((indicator) => {
        // Match by questionId, formKey, and respondentRole
        if (
          indicator.questionId === questionId &&
          indicator.formKey === formKey &&
          (indicator.respondentRole || 'guardian') === respondentRole
        ) {
          // Only include if it has a score (auto-scored)
          // Exclude if the score is undefined/null (would be manual review)
          if (indicator.score !== undefined && indicator.score !== null) {
            autoScoredAnswers.push({
              optionId: indicator.optionId,
              optionLabel: indicator.optionLabel || indicator.selectedOption,
              score: indicator.score,
              freeText: indicator.freeText,
            });
          }
        }
      });
    });
    
    return autoScoredAnswers;
  };
  
  /**
   * Build a unique key for an item for tracking selections.
   */
  const getItemKey = (item) => {
    return `${item.questionId}:${item.optionId}:${item.formKey}:${item.respondentRole || 'guardian'}`;
  };
  
  /**
   * Handle selecting a risk level for a pending item (before confirm).
   * Clicking the same level again will toggle it off (unselect), returning
   * the item to the "Needs Review" state and disabling the Confirm button.
   */
  const handleSelectRiskLevel = (item, riskLevel) => {
    const itemKey = getItemKey(item);
    setPendingSelections(prev => {
      const currentSelection = prev[itemKey];
      
      // If clicking the same level that's already selected, unselect it
      if (currentSelection === riskLevel) {
        const { [itemKey]: _, ...rest } = prev;
        return rest;
      }
      
      // Otherwise, select the new level
      return {
        ...prev,
        [itemKey]: riskLevel
      };
    });
  };
  
  /**
   * Check if all items in a group have pending selections.
   */
  const areAllItemsSelected = (group) => {
    return group.items.every(item => {
      const itemKey = getItemKey(item);
      return !!pendingSelections[itemKey];
    });
  };
  
  /**
   * Handle confirming all selections for a question card.
   * Batch-submits all decisions to the backend.
   * After successful submission, plays an exit animation before notifying the parent.
   */
  const handleConfirmCard = async (group, cardKey) => {
    setSubmittingCards(prev => ({ ...prev, [cardKey]: true }));
    setSubmitError(null);
    
    try {
      // Build the decisions array for batch submission
      const decisions = group.items.map(item => {
        const itemKey = getItemKey(item);
        const riskLevel = pendingSelections[itemKey];
        
        return {
          questionId: item.questionId,
          optionId: item.optionId,
          formKey: item.formKey,
          respondentRole: item.respondentRole || 'guardian',
          riskLevel,
          domain: item.domainHint || 'Vulnerability',
          freeText: item.freeText,
        };
      });
      
      // Call the batch-enabled Cloud Function with retry logic
      // Retries up to 3 times with exponential backoff for transient errors
      const assignManualReviewRisk = httpsCallable(functions, 'rmsAssignManualReviewRisk');
      
      console.log('[ManualReviewContent] Submitting decisions:', { orgId, patientRecordId, decisions });
      
      let result;
      let lastError = null;
      const maxRetries = 3;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          result = await assignManualReviewRisk({
            orgId,
            patientRecordId,
            decisions, // Batch mode
          });
          break; // Success - exit retry loop
        } catch (retryErr) {
          lastError = retryErr;
          const isRetryable = retryErr.code === 'deadline-exceeded' || 
                              retryErr.code === 'unavailable' ||
                              retryErr.message?.includes('deadline-exceeded');
          
          if (isRetryable && attempt < maxRetries - 1) {
            const delay = Math.pow(2, attempt) * 1000;
            console.warn(`[ManualReviewContent] Retrying submission (attempt ${attempt + 2}/${maxRetries}) after ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            throw retryErr; // Non-retryable or max retries reached
          }
        }
      }
      
      if (!result) {
        throw lastError || new Error('Failed to submit after retries');
      }
      
      console.log('[ManualReviewContent] Backend response:', result.data);
      
      // Check for backend failure responses
      if (!result.data?.success) {
        const errorMessage = result.data?.error || result.data?.message || 'Backend returned unsuccessful response';
        console.error('[ManualReviewContent] Backend returned unsuccessful:', result.data);
        setSubmitError(errorMessage);
        return;
      }
      
      if (onRiskAssigned) {
        // Clear pending selections for these items
        const newPendingSelections = { ...pendingSelections };
        group.items.forEach(item => {
          const itemKey = getItemKey(item);
          delete newPendingSelections[itemKey];
        });
        setPendingSelections(newPendingSelections);
        
        // Mark card as exiting to trigger exit animation
        setExitingCards(prev => ({ ...prev, [cardKey]: true }));
        setSubmittingCards(prev => ({ ...prev, [cardKey]: false }));
        
        // Delay the parent notification to allow the exit animation to play
        // Animation duration is 400ms, so we wait 350ms before updating parent state
        setTimeout(() => {
          onRiskAssigned(result.data.riskStratification);
          // Clean up exiting state (card will be removed from list anyway)
          setExitingCards(prev => {
            const newState = { ...prev };
            delete newState[cardKey];
            return newState;
          });
        }, 350);
        
        return; // Exit early since we handled cleanup in setTimeout
      }
    } catch (err) {
      console.error('[ManualReviewContent] Error confirming risk levels:', err);
      // Extract meaningful error message from Firebase error
      const errorMessage = err.message || err.details || 'Failed to confirm risk levels';
      setSubmitError(errorMessage);
    } finally {
      // Only set submitting to false if we didn't already handle it in the success path
      setSubmittingCards(prev => {
        if (prev[cardKey] === true) {
          return { ...prev, [cardKey]: false };
        }
        return prev;
      });
    }
  };
  
  /**
   * Group manual review items by question (questionId + formKey).
   */
  const groupItemsByQuestion = (items) => {
    const groups = {};
    
    items.forEach(item => {
      const key = `${item.questionId}:${item.formKey}`;
      
      if (!groups[key]) {
        groups[key] = {
          questionId: item.questionId,
          questionLabel: item.questionLabel,
          formKey: item.formKey,
          domainHint: item.domainHint,
          submittedAt: item.submittedAt,
          items: [],
          _firstItem: item,
        };
      }
      
      groups[key].items.push(item);
    });
    
    return Object.values(groups);
  };
  
  const guardianGroups = groupItemsByQuestion(guardianItems);
  const schoolGroups = groupItemsByQuestion(schoolItems);
  
  // Check if there are any items left to review
  const hasAnyItems = guardianGroups.length > 0 || schoolGroups.length > 0;
  
  // Tab items for the sidebar
  const tabs = [
    { id: 'guardian', label: 'Parent / Carer', count: guardianGroups.length },
    { id: 'school', label: 'School', count: schoolGroups.length },
  ];
  
  /**
   * Measure both tab contents after render and use the max height.
   * This ensures the container doesn't resize when switching between tabs.
   * Uses minimal delay so height animates in parallel with page transitions.
   * 
   * IMPORTANT: We wait for schemasReady to be true before measuring to ensure
   * content is fully rendered with all options visible. This prevents a two-step
   * height transition when navigating from Overview to Manual Review.
   */
  useLayoutEffect(() => {
    // Wait for schemas to be ready before measuring height
    // This prevents measuring incomplete content before options are loaded
    if (loading || !hasAnyItems || !schemasReady) return;
    
    // Minimal delay to ensure DOM is updated, height animates in parallel
    const measureTimer = setTimeout(() => {
      const guardianHeight = guardianContentRef.current?.scrollHeight || 0;
      const schoolHeight = schoolContentRef.current?.scrollHeight || 0;
      const maxHeight = Math.max(guardianHeight, schoolHeight);
      
      if (maxHeight > 0) {
        setMaxContentHeight(maxHeight);
        // Notify parent of content height for dynamic dialog sizing
        if (onContentHeightChange) {
          onContentHeightChange(maxHeight);
        }
      }
    }, 10);
    
    return () => clearTimeout(measureTimer);
  }, [loading, guardianGroups, schoolGroups, hasAnyItems, schemasReady, onContentHeightChange]);
  
  /**
   * Render a question card for manual review.
   * New design with staged selection and confirm button.
   * Includes smooth exit animation when the card is confirmed and removed.
   */
  const renderQuestionCard = (group, groupIdx, respondentType) => {
    const options = getQuestionOptions(group._firstItem);
    const hasOptions = options && options.length > 0;
    const cardKey = `${respondentType}-${group.questionId}-${groupIdx}`;
    const isExpanded = expandedOptions[cardKey] || false;
    const isSubmitting = submittingCards[cardKey] || false;
    const isExiting = exitingCards[cardKey] || false;
    const allSelected = areAllItemsSelected(group);
    
    return (
      <div
        key={cardKey}
        className={`border rounded-lg overflow-hidden transition-all ${
          isExiting 
            ? 'opacity-0 scale-[0.98] max-h-0 p-0 mb-0 border-transparent' 
            : 'opacity-100 scale-100 max-h-[2000px] p-4 mb-4 last:mb-0 bg-primary/5 border-primary/20'
        }`}
        style={{
          // Smooth exit animation with carefully tuned durations
          // Opacity and scale animate quickly (200ms), height collapses slightly slower (350ms)
          transitionProperty: 'opacity, transform, max-height, padding, margin-bottom, border-color, background-color',
          transitionDuration: isExiting ? '350ms' : '300ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Question text */}
        <div className="text-sm font-medium text-gray-900 mb-3">
          {group.questionLabel || 'Question not available'}
        </div>
        
        {/* View all options accordion */}
        {hasOptions && (
          <div className="mb-3">
            <button
              type="button"
              onClick={() => toggleOptionsExpanded(cardKey)}
              className="text-xs text-gray-500 cursor-pointer hover:text-gray-600 flex items-center gap-1 transition-colors"
            >
              <ChevronDownIcon 
                className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
              />
              {isExpanded ? 'Hide' : 'View all'} {options.length} options
            </button>
            <div 
              className="grid transition-all duration-200 ease-in-out"
              style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {options.map((opt) => {
                    const isSelected = group.items.some(item => item.optionId === opt.optionId);
                    return (
                      <span
                        key={opt.optionId}
                        className={`text-xs px-2 py-1 rounded ${
                          isSelected
                            ? 'bg-primary/10 border border-primary/30 text-primary font-medium'
                            : 'bg-gray-100 border border-gray-200 text-gray-500'
                        }`}
                      >
                        {opt.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Auto-scored context answers - show other selected answers that don't need review */}
        {(() => {
          // Get the first item to determine respondent role for the question
          const firstItem = group.items[0];
          const contextAnswers = getAutoScoredAnswersForQuestion(
            group.questionId,
            group.formKey,
            firstItem?.respondentRole || 'guardian'
          );
          
          // Filter out any that are also in the manual review items (to avoid duplicates)
          const manualReviewOptionIds = new Set(group.items.map(item => item.optionId));
          const uniqueContextAnswers = contextAnswers.filter(
            answer => !manualReviewOptionIds.has(answer.optionId)
          );
          
          if (uniqueContextAnswers.length === 0) return null;
          
          return (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2 font-medium">Other answers selected (auto-scored):</p>
              <div className="space-y-2">
                {uniqueContextAnswers.map((answer, idx) => {
                  const scoreLabel = SCORE_LABELS[answer.score] || SCORE_LABELS[1];
                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
                    >
                      {/* Risk level badge - same fixed width as manual review items */}
                      <span
                        className="flex-shrink-0 inline-flex items-center justify-center w-24 h-6 rounded text-[11px] font-semibold"
                        style={{
                          backgroundColor: scoreLabel.bg,
                          color: scoreLabel.captionColor,
                          border: `1px solid ${scoreLabel.captionColor}30`,
                        }}
                      >
                        {scoreLabel.label}
                      </span>
                      
                      {/* Answer text */}
                      <p className="text-sm text-gray-700">
                        {answer.optionLabel}{answer.freeText && `: `}
                        {answer.freeText && (
                          <span className="italic text-gray-500">"{answer.freeText}"</span>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        
        {/* Items needing manual review - layout with badges on left */}
        <div className="space-y-4">
          <p className="text-xs text-gray-500 mb-2 font-medium">Answers needing review:</p>
          {group.items.map((item, itemIdx) => {
            const itemKey = getItemKey(item);
            const selectedLevel = pendingSelections[itemKey];
            
            // Get the config for the selected level (or use needsReview if not selected)
            const badgeConfig = selectedLevel
              ? RISK_LEVEL_OPTIONS.find(opt => opt.level === selectedLevel)
              : null;
            
            return (
              <div
                key={itemIdx}
                className="bg-white border border-gray-200 rounded-lg p-3 transition-all duration-300"
              >
                {/* Answer row: Badge on LEFT, answer text on RIGHT */}
                <div className="flex items-start gap-3 mb-3">
                  {/* Badge showing current selection or "Needs Review" */}
                  <span
                    className="flex-shrink-0 inline-flex items-center justify-center w-24 h-6 rounded text-[11px] font-semibold"
                    style={badgeConfig ? {
                      backgroundColor: badgeConfig.bgColor,
                      color: badgeConfig.textColor,
                      border: `1px solid ${badgeConfig.borderColor}`,
                    } : {
                      backgroundColor: SCORE_LABELS['needsReview'].bg,
                      color: SCORE_LABELS['needsReview'].captionColor,
                      border: `1px solid ${SCORE_LABELS['needsReview'].captionColor}40`,
                    }}
                  >
                    {selectedLevel || 'Needs Review'}
                  </span>
                  
                  {/* Answer text */}
                  <div className="flex-grow">
                    <p className="text-sm text-gray-800">
                      {item.optionLabel}{item.freeText && `: `}
                      {item.freeText && (
                        <span className="italic text-gray-600">"{item.freeText}"</span>
                      )}
                    </p>
                  </div>
                </div>
                
                {/* Risk level selection buttons */}
                <div className="pl-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {/* Risk level options (Urgent, High, Medium, Standard) */}
                    {/* All buttons have consistent width (min-w-[76px]) to match "Not relevant" */}
                    {RISK_LEVEL_OPTIONS.filter(opt => opt.score > 0).map((option) => {
                      const isSelected = selectedLevel === option.level;
                      return (
                        <button
                          key={option.level}
                          type="button"
                          disabled={isSubmitting || readOnly}
                          onClick={() => handleSelectRiskLevel(item, option.level)}
                          className={`min-w-[76px] px-2.5 py-1.5 text-xs font-medium rounded border transition-all hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed text-center ${
                            isSelected ? 'ring-2 ring-offset-1 ring-primary' : ''
                          }`}
                          style={{
                            backgroundColor: isSelected ? option.bgColor : '#f9fafb',
                            color: isSelected ? option.textColor : '#6b7280',
                            borderColor: isSelected ? option.textColor : '#d1d5db',
                          }}
                        >
                          {option.level}
                        </button>
                      );
                    })}
                    
                    {/* "or" separator */}
                    <span className="text-xs text-gray-400 px-1">or</span>
                    
                    {/* Not relevant option */}
                    {RISK_LEVEL_OPTIONS.filter(opt => opt.score === 0).map((option) => {
                      const isSelected = selectedLevel === option.level;
                      return (
                        <button
                          key={option.level}
                          type="button"
                          disabled={isSubmitting || readOnly}
                          onClick={() => handleSelectRiskLevel(item, option.level)}
                          className={`min-w-[76px] px-2.5 py-1.5 text-xs font-medium rounded border transition-all hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed text-center ${
                            isSelected ? 'ring-2 ring-offset-1 ring-primary' : ''
                          }`}
                          style={{
                            backgroundColor: isSelected ? option.bgColor : '#f9fafb',
                            color: isSelected ? option.textColor : '#6b7280',
                            borderColor: isSelected ? option.textColor : '#d1d5db',
                          }}
                        >
                          {option.level}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Footer with domain info, form name, submitted date, and Confirm button */}
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
          <div className="text-xs text-gray-400">
            {group.domainHint && (
              <span className="font-medium">Domain: {group.domainHint}</span>
            )}
            {group.formKey && group.domainHint && <span className="mx-1">•</span>}
            {group.formKey && (
              <span>
                {formKeyToTitle(group.formKey)}
                {group.submittedAt && (
                  <span>
                    {' '}submitted on {new Date(group.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(group.submittedAt).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                )}
              </span>
            )}
          </div>
          
          {/* Confirm button */}
          <button
            type="button"
            disabled={!allSelected || isSubmitting || readOnly}
            onClick={() => handleConfirmCard(group, cardKey)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              allSelected && !isSubmitting
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Confirming...' : 'Confirm'}
          </button>
        </div>
        
        {/* Error message */}
        {submitError && submittingCards[cardKey] === false && (
          <p className="text-xs text-red-600 mt-2">{submitError}</p>
        )}
      </div>
    );
  };
  
  // Render with sidebar navigation (matching DomainDetailContent layout)
  return (
    <div className="flex overflow-hidden h-full">
      {/* Sidebar Navigation */}
      <div className="w-52 flex-shrink-0 border-r border-gray-200 bg-gradient-to-b from-primary/5 to-primary/10">
        <div className="p-4 pt-6">
          <nav className="space-y-1.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between
                  transition-all duration-200 ease-in-out
                  ${selectedTab === tab.id && !showMethodology
                    ? 'bg-white shadow-sm border border-gray-200 text-primary'
                    : 'text-gray-600 hover:bg-white/70 hover:text-gray-800 border border-transparent'
                  }`}
              >
                <span className={`font-medium transition-colors duration-200 ${selectedTab === tab.id && !showMethodology ? 'text-primary' : 'text-gray-600'}`}>
                  {tab.label}
                </span>
                <span 
                  className={`text-xs min-w-[28px] h-5 flex items-center justify-center rounded-full
                    transition-colors duration-200 ease-in-out
                    ${selectedTab === tab.id && !showMethodology
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'bg-gray-200 text-gray-500'
                    }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
            
            {/* Divider between respondent tabs and methodology */}
            <div className="border-t border-gray-200/70 my-2"></div>
            
            {/* Methodology button as nav item */}
            <button
              type="button"
              onClick={() => setShowMethodology(true)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center gap-2
                transition-all duration-200 ease-in-out
                ${showMethodology
                  ? 'bg-white shadow-sm border border-gray-200 text-primary'
                  : 'text-gray-600 hover:bg-white/70 hover:text-gray-800 border border-transparent'
                }`}
            >
              <InformationCircleIcon className="w-4 h-4" />
              <span className={`font-medium transition-colors duration-200 ${showMethodology ? 'text-primary' : 'text-gray-600'}`}>
                Methodology
              </span>
            </button>
          </nav>
        </div>
      </div>
      
      {/* Main Content Area - always allow vertical scrolling when content overflows */}
      <div 
        ref={contentAreaRef}
        className="flex-1 overflow-x-hidden overflow-y-auto p-6 relative"
      >
        {/* ===== METHODOLOGY VIEW ===== */}
        {showMethodology && (
          <div className="transition-opacity duration-200">            
                        
            {/* Tier 1: Urgent */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                  Tier 1: Urgent Priority
                </span>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 font-medium mb-2">Safety (Immediate danger to self or others):</p>
                <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                  <li>Active suicidality or serious self-harm</li>
                </ul>
              </div>
            </div>
            
            {/* Tier 2: High Risk */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                  Tier 2: High Risk
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3 italic">Prioritise if ANY of the following are present:</p>
              
              <div className="space-y-3">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 font-medium mb-2">Safety (Imminent risk of harm to self or others):</p>
                  <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                    <li>Recent suicide attempt</li>
                    <li>Reckless behaviour</li>
                    <li>Severe accidental injuries</li>
                    <li>Under treatment for substance misuse disorder as a coping mechanism</li>
                    <li>Ongoing bullying, victimisation, harassment or perpetration leading to significant distress or school refusal</li>
                    <li>Reckless driving with recent citations/accidents</li>
                  </ul>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 font-medium mb-2">Function and Stability (Risk of catastrophic failure in a major life domain):</p>
                  <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                    <li>Permanent school exclusion or processed for permanent exclusion</li>
                    <li>Complete school refusal ({'>'}50% absence over a sustained period) directly linked to distress</li>
                    <li>Imminent family breakdown (e.g., being removed from the home due to behaviours linked to unmet need)</li>
                    <li>Acute breakdown in caring arrangements (e.g. placement disruption, parental separation linked to symptoms)</li>
                    <li>Risk of entering care or escalating involvement with social services</li>
                    <li>Acute psychiatric hospitalisation of the child or a primary carer</li>
                  </ul>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 font-medium mb-2">Vulnerability (Systemic factors that increase overall risk):</p>
                  <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                    <li>No stable support network (e.g. isolated from family, peer rejection, frequent school changes)</li>
                    <li>Young carer responsibilities causing high distress</li>
                    <li>Subject of a Child Protection Plan</li>
                    <li>Looked-After Child (in care)</li>
                    <li>Severe, pervasive social isolation</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Tier 3: Medium Risk */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
                  Tier 3: Medium Risk
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3 italic">
                Prioritise for Tier 3 if: At least 2 indicators across different domains, or 3 or more indicators within a single domain, and no Tier 1 or Tier 2 criteria met.
              </p>
              
              <div className="space-y-3">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 font-medium mb-2">Safety (Imminent risk of harm to self or others):</p>
                  <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                    <li>History of self-harm or suicidal ideation (not current or active)</li>
                    <li>Occasional reckless or impulsive behaviour with safety implications (e.g. climbing, running away, risky online activity)</li>
                    <li>Episodic aggression or property destruction</li>
                  </ul>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 font-medium mb-2">Function and Stability (Risk of catastrophic failure in a major life domain):</p>
                  <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                    <li>Persistent school underperformance, frequent detentions, repeated suspensions, or risk of exclusion</li>
                    <li>On the verge of permanent exclusion from education</li>
                    <li>Chronic family conflict or strained relationships with parents/carers</li>
                    <li>Social isolation (no friends, excluded from peer group)</li>
                  </ul>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 font-medium mb-2">Vulnerability (Systemic factors that increase overall risk):</p>
                  <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                    <li>Limited but present social/family support</li>
                    <li>Family financial stress, parental mental health difficulties, or parental substance misuse</li>
                    <li>Subject of a Child in Need plan</li>
                    <li>Limited peer friendships and social struggles</li>
                    <li>Expulsion from community clubs/activities due to behaviour</li>
                    <li>History of trauma or adverse childhood experiences (ACEs)</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Tier 4: Standard Risk */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                  Tier 4: Standard Risk
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3 italic">
                Target: Routine Waitlist. Activation: Difficulties are significant but contained within one primary domain, OR are managed by strong protective factors. The child's situation is stable with no evidence of escalating risk or acute crisis.
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 font-medium mb-2">Characteristics:</p>
                <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                  <li><span className="font-medium">Stable and Supported:</span> Challenges exist within a generally stable and supportive school and family environment</li>
                  <li><span className="font-medium">No Multi-Domain Risk:</span> Does not meet the criteria for Tier 3 (i.e., does not have significant, co-occurring risks across multiple domains)</li>
                  <li><span className="font-medium">No Acute Safety Concerns:</span> No history of self-harm, severe aggression, or dangerous impulsivity</li>
                  <li><span className="font-medium">Protective Factors Present:</span> The child and family are engaged and have the capacity to utilise support when offered</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {/* ===== REVIEW CONTENT ===== */}
        {!showMethodology && loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshIcon className="w-6 h-6 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-600">Loading question details...</span>
          </div>
        )}
        
        {!showMethodology && !loading && !hasAnyItems && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <InformationCircleIcon className="w-12 h-12 mb-3 text-green-500" />
            <p className="text-lg font-medium text-green-700">All items have been reviewed!</p>
            <p className="text-sm text-gray-500 mt-1">You can navigate back to the overview.</p>
          </div>
        )}
        
        {!showMethodology && !loading && hasAnyItems && (
          <div className="relative">
            {readOnly && (
              <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                These items are read-only. Only the clinician who has started triage for
                this patient can review and flag them — start triage to make changes.
              </div>
            )}
            {/* Guardian tab content */}
            <div 
              ref={guardianContentRef}
              className={`transition-opacity duration-200 ${
                selectedTab === 'guardian' 
                  ? 'opacity-100' 
                  : 'opacity-0 absolute inset-0 pointer-events-none'
              }`}
            >
              {/* Summary headline */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{getRespondentRoleLabel('guardian')}</span>
                  {guardianGroups.length > 0 
                    ? ` has ${guardianGroups.length} question${guardianGroups.length > 1 ? 's' : ''} requiring manual review`
                    : ' has no items requiring manual review'
                  }
                </p>
              </div>
              
              {guardianGroups.length > 0 ? (
                <div>
                  {guardianGroups.map((group, idx) => renderQuestionCard(group, idx, 'guardian'))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <InformationCircleIcon className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm">No items to review for {getRespondentRoleLabel('guardian')}</p>
                </div>
              )}
            </div>
            
            {/* School tab content */}
            <div 
              ref={schoolContentRef}
              className={`transition-opacity duration-200 ${
                selectedTab === 'school' 
                  ? 'opacity-100' 
                  : 'opacity-0 absolute inset-0 pointer-events-none'
              }`}
            >
              {/* Summary headline */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{getRespondentRoleLabel('school_user')}</span>
                  {schoolGroups.length > 0 
                    ? ` has ${schoolGroups.length} question${schoolGroups.length > 1 ? 's' : ''} requiring manual review`
                    : ' has no items requiring manual review'
                  }
                </p>
              </div>
              
              {schoolGroups.length > 0 ? (
                <div>
                  {schoolGroups.map((group, idx) => renderQuestionCard(group, idx, 'school'))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <InformationCircleIcon className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm">No items to review for {getRespondentRoleLabel('school_user')}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


/**
 * Domain card component
 * ---------------------
 * Displays a risk domain with its gauge and a summarized bullet list of indicators
 * grouped by respondent. Clicking "View details" triggers the onViewDetails callback
 * which allows the parent to handle navigation (e.g., switching to a detail view).
 * 
 * Props:
 *   - domainName: string - Name of the risk domain
 *   - summary: object - Domain summary with indicators
 *   - respondentNames: object - { guardian: { firstName, lastName, relationshipLabel }, school_user: { firstName, lastName, role } }
 *   - onViewDetails: function - Callback when "View details" is clicked
 */
const DomainCard = ({ domainName, summary, respondentNames, onViewDetails }) => {
  
  const domainConfig = DOMAIN_CONFIG[domainName] || {
    description: '',
    icon: InformationCircleIcon,
    order: 99,
  };
  
  const maxScore = summary?.maxScore || 1;
  const indicators = summary?.indicators || [];
  
  // Group indicators by respondent and create summary
  const grouped = groupIndicatorsByRespondent(indicators);
  const guardianSummary = summarizeByScore(grouped.guardian);
  const schoolSummary = summarizeByScore(grouped.school);

  /**
   * Get the role label for a respondent (e.g., "Foster parent", "Teacher").
   * Uses the relationshipLabel for guardians or role for school users.
   * Falls back to a generic label if not available.
   * 
   * @param respondentInfo - { firstName, lastName, relationshipLabel/role } or null
   * @param fallbackLabel - Fallback label if respondentInfo is not available (e.g., "Parent / Carer")
   */
  const getRespondentRoleLabel = (respondentInfo, fallbackLabel) => {
    // Use relationshipLabel for guardians, role for school users
    const roleLabel = respondentInfo?.relationshipLabel || respondentInfo?.role || null;
    return roleLabel || fallbackLabel;
  };
  
  /**
   * Render a summary line for a respondent showing what risk indicators they reported.
   * Format: "{Role in bold} reported risk indicators: X [Urgent], Y [High] and Z [Medium]"
   * OR: "{Role in bold} reported no elevated risk indicators"
   * 
   * Always renders a line (even if no elevated indicators), since we want 2 bullet points per domain.
   * 
   * @param scoreSummary - Array of { score, label, count } objects
   * @param respondentInfo - { firstName, lastName, relationshipLabel/role } or null
   * @param fallbackLabel - Fallback label if respondentInfo is not available (e.g., "Parent / Carer")
   */
  const renderSummaryLine = (scoreSummary, respondentInfo, fallbackLabel) => {
    const roleLabel = getRespondentRoleLabel(respondentInfo, fallbackLabel);
    
    // If no elevated indicators, show "reported no elevated risk indicators"
    if (scoreSummary.length === 0) {
      return (
        <li className="flex items-baseline gap-1.5 text-sm text-gray-700">
          <span className="text-gray-500 flex-shrink-0">•</span>
          <span className="flex flex-wrap items-baseline gap-1.5">
            <span className="font-semibold text-gray-900">{roleLabel}</span>
            <span className="text-gray-600">reported no elevated risk indicators</span>
          </span>
        </li>
      );
    }
    
    // Build parts with count and styled label badge
    const parts = scoreSummary.map(({ score, label, count }) => {
      const config = SCORE_LABELS[score];
      return (
        <span key={score} className="inline-flex items-center gap-1">
          <span className="font-semibold">{count}</span>
          <span
            className="px-1.5 py-0.5 rounded text-xs font-medium"
            style={{ backgroundColor: config.bg, color: config.captionColor }}
          >
            {label}
          </span>
        </span>
      );
    });
    
    // Build the parts list with natural language conjunctions
    // 1 item: "A"
    // 2 items: "A and B"
    // 3+ items: "A, B, and C"
    const buildConjoinedParts = () => {
      if (parts.length === 1) {
        return parts;
      }
      if (parts.length === 2) {
        return [parts[0], <span key="and" className="text-gray-600">, </span>, parts[1]];
      }
      // 3+ items: "A, B, and C"
      const result = [];
      parts.forEach((part, idx) => {
        if (idx === 0) {
          result.push(part);
        } else if (idx === parts.length - 1) {
          // Last item: " and C"
          result.push(<span key={`and-${idx}`} className="text-gray-600"> and </span>);
          result.push(part);
        } else {
          // Middle items: ", B"
          result.push(<span key={`comma-${idx}`} className="text-gray-600">, </span>);
          result.push(part);
        }
      });
      return result;
    };
    
    return (
      <li className="flex items-baseline gap-1.5 text-sm text-gray-700">
        <span className="text-gray-500 flex-shrink-0">•</span>
        <span className="flex flex-wrap items-baseline gap-1.5">
          <span className="font-semibold text-gray-900">{roleLabel}</span>
          <span className="text-gray-600">reported risk indicators:</span>
          {buildConjoinedParts()}
        </span>
      </li>
    );
  };

  return (
    <>
      {/* Domain section without bordered card - uses horizontal divider instead */}
      <div className="py-5 border-b border-gray-200 last:border-b-0">
        {/* Domain layout: 2/3 for content, 1/3 for gauge */}
        <div className="flex items-start gap-6">
          {/* Left 3/4: Domain info and bullet points */}
          <div className="w-3/4">
            <h4 className="text-lg font-bold text-gray-900 mb-1">{domainName}</h4>
            <p className="text-sm text-gray-500 mb-3">{domainConfig.description}</p>
            
            {/* Summary bullet points - always show 2 lines (one for guardian, one for school) */}
            <ul className="space-y-2 mb-3 list-none">
              {renderSummaryLine(guardianSummary, respondentNames?.guardian, 'Parent / Carer')}
              {renderSummaryLine(schoolSummary, respondentNames?.school_user, 'School Representative')}
            </ul>
            
            {/* View details button */}
            <button
              type="button"
              onClick={() => onViewDetails && onViewDetails(domainName, indicators)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <SearchIcon className="w-4 h-4" />
              View details
            </button>
          </div>
          
          {/* Right 1/4: Domain gauge - vertically centered within its section */}
          <div className="w-1/4 flex justify-center items-center self-center">
            <RmsGaugeChart
              riskLevel={scoreToRiskLevel(maxScore)}
              size={100}
              thickness={24}
              segmentColor={SCORE_LABELS[maxScore]?.segmentColor || '#4ade80'}
              captionText={scoreToRiskLevel(maxScore)}
              captionColor={SCORE_LABELS[maxScore]?.captionColor || '#16a34a'}
            />
          </div>
        </div>
      </div>
    </>
  );
};


// Note: RISK_LEVEL_OPTIONS is now imported from '../../utils/riskStratification'

/**
 * Main Dialog Component
 * 
 * Props:
 *   - isOpen: boolean - Controls dialog visibility
 *   - onClose: () => void - Callback to close the dialog
 *   - riskData: object - The riskStratification object from the patient record
 *   - orgId: string - Organisation ID for fetching schemas in detail views
 *   - patientRecordId: string - Patient record ID for manual review assignment
 *   - respondentNames: object - { guardian: { firstName, lastName, relationshipLabel }, school_user: { firstName, lastName, role } }
 *   - onRiskDataUpdated: function - Callback when risk data is updated (e.g., after manual review assignment)
 */
export default function RiskStratificationDialog({
  isOpen,
  onClose,
  riskData,
  orgId,
  patientRecordId,
  respondentNames,
  onRiskDataUpdated,
  // TD-51 (A18): only the self-assigned, committed triage clinician may flag the
  // manual-review statements that inform priority. Defaults to read-only so any
  // caller that doesn't opt in cannot write decisions.
  canReview = false,
}) {
  const [isRawJsonOpen, setIsRawJsonOpen] = useState(false);
  
  // Navigation state for switching between overview and domain detail views
  // currentView: 'overview' | 'domainDetail' | 'manualReviewDetail'
  const [currentView, setCurrentView] = useState('overview');
  // When viewing domain details, store the domain name and its indicators
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  // Initial tab to show in Manual Review (when navigating from indicator details)
  // null = use default logic, 'guardian' or 'school' = force that tab
  const [manualReviewInitialTab, setManualReviewInitialTab] = useState(null);
  
  // Dynamic height management for smooth transitions between views
  // contentHeight tracks the current view's content height for smooth dialog resizing
  const [contentHeight, setContentHeight] = useState(null);
  const overviewRef = useRef(null);
  const manualReviewRef = useRef(null);
  
  // Maximum allowed content height (65vh minus header ~65px)
  const MAX_CONTENT_HEIGHT = typeof window !== 'undefined' 
    ? Math.floor(window.innerHeight * 0.65) - 65 
    : 500;
  
  /**
   * Handler for when a domain's "View details" button is clicked.
   * Navigates from the overview to the domain detail view within the same dialog.
   * 
   * @param domainName - Name of the domain to view details for
   * @param indicators - Array of indicators for the selected domain
   */
  const handleViewDomainDetails = (domainName, indicators) => {
    setSelectedDomain(domainName);
    setSelectedIndicators(indicators || []);
    setCurrentView('domainDetail');
    // Immediately set height to max so expansion starts in parallel with page transition
    // The actual height will be refined once DomainDetailContent measures its content
    setContentHeight(MAX_CONTENT_HEIGHT);
  };
  
  /**
   * Handler for navigating back to the overview from a detail view.
   * Resets the selected domain/indicators and switches view back to overview.
   */
  const handleBackToOverview = () => {
    setCurrentView('overview');
    // Small delay before clearing selected data to allow smooth transition
    setTimeout(() => {
      setSelectedDomain(null);
      setSelectedIndicators([]);
    }, 300);
  };
  
  /**
   * Handler for when "View details" is clicked on the Manual Review card.
   * Navigates from the overview to the manual review view within the same dialog.
   */
  const handleViewManualReview = () => {
    setCurrentView('manualReview');
    setManualReviewInitialTab(null); // No specific tab - use default logic
    // Immediately set height to max so expansion starts in parallel with page transition
    // The actual height will be refined once ManualReviewContent measures its content
    setContentHeight(MAX_CONTENT_HEIGHT);
  };
  
  /**
   * Handler for navigating to Manual Review with a specific tab pre-selected.
   * Called from DomainDetailContent when user clicks on a "Needs Review" badge.
   * 
   * @param {string} respondentRole - 'guardian' or 'school_user' to determine which tab to show
   */
  const handleNavigateToManualReviewWithTab = useCallback((respondentRole) => {
    // Map respondentRole to tab id
    const tabId = respondentRole === 'school_user' ? 'school' : 'guardian';
    setManualReviewInitialTab(tabId);
    setCurrentView('manualReview');
    setContentHeight(MAX_CONTENT_HEIGHT);
    // Clear domain selection since we're leaving the detail view
    setTimeout(() => {
      setSelectedDomain(null);
      setSelectedIndicators([]);
    }, 300);
  }, [MAX_CONTENT_HEIGHT]);
  
  /**
   * Callback for DomainDetailContent to report its content height.
   * Used to dynamically size the dialog based on content.
   */
  const handleDomainContentHeightChange = useCallback((height) => {
    // Cap at max height
    const cappedHeight = Math.min(height, MAX_CONTENT_HEIGHT);
    setContentHeight(cappedHeight);
  }, [MAX_CONTENT_HEIGHT]);
  
  /**
   * Callback for ManualReviewContent to report its content height.
   * Used to dynamically size the dialog based on content.
   */
  const handleManualReviewContentHeightChange = useCallback((height) => {
    // Cap at max height
    const cappedHeight = Math.min(height, MAX_CONTENT_HEIGHT);
    setContentHeight(cappedHeight);
  }, [MAX_CONTENT_HEIGHT]);
  
  /**
   * Measure OVERVIEW content height when view changes to overview.
   * Domain detail and manual review heights are handled by their respective
   * child component callbacks (onContentHeightChange), so we only measure overview here.
   * Uses minimal delay to allow DOM to update, then measures immediately
   * so height transition can happen IN PARALLEL with the page transition.
   */
  useLayoutEffect(() => {
    if (!isOpen) return;
    
    // Only measure overview - other views report their own heights via callbacks
    if (currentView !== 'overview') return;
    
    // Minimal delay (10ms) just to ensure DOM is updated
    // Height change will animate in parallel with the page opacity/translate transition
    const measureTimer = setTimeout(() => {
      if (overviewRef.current) {
        // Measure the actual content height, not the scrollHeight of the container
        const contentDiv = overviewRef.current.querySelector('.flex.gap-6');
        const height = contentDiv ? contentDiv.offsetHeight + 48 : overviewRef.current.scrollHeight; // 48px for p-6 padding
        
        const cappedHeight = Math.min(height, MAX_CONTENT_HEIGHT);
        setContentHeight(cappedHeight);
      }
    }, 10); // Minimal delay - height animates in parallel with page transition
    
    return () => clearTimeout(measureTimer);
  }, [currentView, isOpen, riskData, MAX_CONTENT_HEIGHT]);
  
  // Reset navigation state when dialog is closed
  React.useEffect(() => {
    if (!isOpen) {
      setCurrentView('overview');
      setSelectedDomain(null);
      setSelectedIndicators([]);
      setManualReviewInitialTab(null);
    }
  }, [isOpen]);
  
  // Memoize domainSummaries to prevent dependency array changes on every render
  // This ensures stable references for the dependent useMemo hooks below
  const domainSummaries = useMemo(
    () => riskData?.domainSummaries || {},
    [riskData?.domainSummaries]
  );
  
  // Calculate elevated indicator counts and highest risk level (memoized for performance)
  // Uses shared utility function from riskStratification.js
  // Note: Placed before early return to comply with React hooks rules
  const indicatorStats = useMemo(
    () => calculateIndicatorStats(domainSummaries),
    [domainSummaries]
  );

  // Sort domains by configured order (memoized for performance)
  // Uses shared utility function from riskStratification.js
  // Note: Placed before early return to comply with React hooks rules
  const sortedDomains = useMemo(
    () => sortDomainsByOrder(Object.entries(domainSummaries)),
    [domainSummaries]
  );

  // Early return if no risk data - placed AFTER all hooks to comply with React rules
  if (!riskData) return null;

  const tier = riskData.tier || 4;
  const config = TIER_CONFIG[tier] || TIER_CONFIG[4];
  
  // Manual review items (answers requiring clinician assessment)
  const manualReviewItems = riskData.manualReviewItems || [];
  const manualReviewCounts = riskData.manualReviewCounts || { guardian: 0, school_user: 0 };
  const totalManualReview = (manualReviewCounts.guardian || 0) + (manualReviewCounts.school_user || 0);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
        <Dialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-6xl bg-white rounded-xl shadow-xl z-50 overflow-hidden flex flex-col transition-all duration-300 ease-in-out"
          style={{
            // Dynamic height based on content, with smooth transition
            // Falls back to auto if contentHeight not yet measured
            height: contentHeight ? `${contentHeight + 65}px` : 'auto',
            maxHeight: '65vh',
            minHeight: '50vh',
          }}
        >
          
          {/* Header - Three-column layout: Back button (left), Title (center), Close button (right) */}
          <div className="grid grid-cols-3 items-center px-6 py-4 border-b border-gray-200">
            {/* Left: Back button */}
            <div className="flex justify-start">
              {currentView === 'overview' ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  Back to Referral Overview
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleBackToOverview}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  Back to Risk Stratification Overview
                </button>
              )}
            </div>
            
            {/* Center: Title */}
            <Dialog.Title className="text-xl font-semibold text-gray-900 text-center">
              {currentView === 'overview' && 'Risk Stratification'}
              {currentView === 'domainDetail' && `${selectedDomain}`}
              {currentView === 'manualReview' && 'Manual Review'}
            </Dialog.Title>
            
            {/* Right: Close button */}
            <div className="flex justify-end">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="p-1 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </Dialog.Close>
            </div>
          </div>
          
          {/* Content - switches between views with smooth transitions */}
          {/* flex-1 allows content to fill remaining space after header */}
          {/* overflow-hidden here - individual views handle their own scrolling */}
          <div className="flex-1 overflow-hidden relative">
            
            {/* ===== OVERVIEW VIEW ===== */}
            {/* Always allow vertical scrolling when content overflows (e.g., when Raw JSON accordion is expanded) */}
            <div 
              ref={overviewRef}
              className={`h-full transition-all duration-300 ease-in-out overflow-y-auto ${
                currentView === 'overview' 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-8 absolute inset-0 pointer-events-none'
              }`}
            >
              <div className="flex gap-6 p-6">
                {/* Left panel: Overall tier (1/3 width) - vertically centered */}
                <div className="w-1/3 flex-shrink-0 pr-6 border-r border-gray-200">
                  <div className="flex flex-col items-center justify-center h-full">
                    {/* Gauge Chart */}
                    <div className="mb-4">
                      <RmsGaugeChart
                        riskLevel={tierToRiskLevel(tier)}
                        size={200}
                        thickness={50}
                        segmentColor={config.segmentColor}
                        captionText={config.label}
                        captionColor={config.captionColor}
                      />
                    </div>
                    
                    {/* Dynamic bullet points summarising risk indicators and manual review items */}
                    <ul className="text-sm text-gray-600 mb-4 space-y-1.5 w-42">
                      {/* Elevated risk indicators summary - shows count of highest level only */}
                      {indicatorStats.highestLevelCount > 0 ? (
                        <li className="flex items-center gap-2">
                          <span className="text-gray-500">•</span>
                          <span>
                            <span className="font-semibold">{indicatorStats.highestLevelCount}</span>
                            {' '}
                            <span
                              className="px-1.5 py-0.5 rounded text-xs font-medium"
                              style={getBadgeStyleForLevel(indicatorStats.highestLevel)}
                            >
                              {indicatorStats.highestLevel}
                            </span>
                            {' '}risk indicator{indicatorStats.highestLevelCount !== 1 ? 's' : ''} reported
                          </span>
                        </li>
                      ) : (
                        <li className="flex items-center gap-2">
                          <span className="text-gray-500">•</span>
                          <span>No elevated risk indicators reported</span>
                        </li>
                      )}
                      
                      {/* Manual review items summary (only show if there are any) */}
                      {totalManualReview > 0 && (
                        <li className="flex items-center gap-2">
                          <span className="text-gray-500">•</span>
                          <span>
                            <span className="font-semibold">{totalManualReview}</span>
                            {' '}answer{totalManualReview !== 1 ? 's' : ''} needing manual review
                          </span>
                          <InfoTooltip
                            content="Clinical review is required where respondents have selected an answer option that cannot be automatically scored"
                            side="right"
                            maxWidth="280px"
                          />
                        </li>
                      )}
                    </ul>
                    
                    {/* Review answers button (only show if there are manual review items) */}
                    {totalManualReview > 0 && (
                      <button
                        type="button"
                        onClick={handleViewManualReview}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors mb-4"
                      >
                        Review answers
                      </button>
                    )}
                    
                    {/* Last updated timestamp */}
                    {riskData.calculatedAt && (
                      <p className="text-xs text-gray-500 italic mb-4">
                        Last updated on {new Date(riskData.calculatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(riskData.calculatedAt).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Right panel: Domain breakdown (2/3 width) */}
                <div className="w-2/3">
                  
                  {sortedDomains.length === 0 ? (
                    <p className="text-sm text-gray-500">No domain data available</p>
                  ) : (
                    sortedDomains.map(([domainName, summary]) => (
                      <DomainCard
                        key={domainName}
                        domainName={domainName}
                        summary={summary}
                        respondentNames={respondentNames}
                        onViewDetails={handleViewDomainDetails}
                      />
                    ))
                  )}
                </div>
              </div>
              
              {/* Raw JSON accordion (dev/debug) - only shown in local development only */}
              {isDev && (
                <div className="border-t border-gray-200 p-4">
                  <button
                    type="button"
                    onClick={() => setIsRawJsonOpen(!isRawJsonOpen)}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
                  >
                    {isRawJsonOpen ? (
                      <ChevronUpIcon className="w-4 h-4" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4" />
                    )}
                    Raw Data (Dev)
                  </button>
                  
                  {isRawJsonOpen && (
                    <div className="mt-3 bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                        {JSON.stringify(riskData, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* ===== DOMAIN DETAIL VIEW ===== */}
            <div 
              className={`h-full transition-all duration-300 ease-in-out ${
                currentView === 'domainDetail' 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-8 absolute inset-0 pointer-events-none'
              }`}
            >
              {currentView === 'domainDetail' && selectedDomain && (
                <DomainDetailContent
                  domainName={selectedDomain}
                  indicators={selectedIndicators}
                  orgId={orgId}
                  respondentNames={respondentNames}
                  pendingReviewByQuestion={riskData?.pendingReviewByQuestion || {}}
                  onContentHeightChange={handleDomainContentHeightChange}
                  onNavigateToManualReview={handleNavigateToManualReviewWithTab}
                />
              )}
            </div>
            
            {/* ===== MANUAL REVIEW VIEW ===== */}
            {/* Manual review now has its own sidebar layout, so no scrolling here */}
            <div 
              ref={manualReviewRef}
              className={`h-full transition-all duration-300 ease-in-out overflow-hidden ${
                currentView === 'manualReview' 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-8 absolute inset-0 pointer-events-none'
              }`}
            >
              {currentView === 'manualReview' && (
                <ManualReviewContent
                  manualReviewItems={manualReviewItems}
                  riskData={riskData}
                  respondentNames={respondentNames}
                  orgId={orgId}
                  patientRecordId={patientRecordId}
                  onRiskAssigned={onRiskDataUpdated}
                  onContentHeightChange={handleManualReviewContentHeightChange}
                  initialTab={manualReviewInitialTab}
                  canReview={canReview}
                />
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
